import React, { useContext, useMemo } from "react";
import clsx from "clsx";

import { Context } from "../../../../teacherStore";
import { getStudentAnswersByQuestion } from "../../../../libs/ActiveLearning";
import styles from "../ActiveLearningLessonAnswerData.module.scss";

interface ILesson4Pros {
  materialCode: string;
  lessonNo: number;
}

export const Lesson4: React.FC<ILesson4Pros> = ({ materialCode, lessonNo }) => {
  const { state } = useContext(Context);
  const activeLearningStudentAnswers = state.active_learning_student_answers;

  const answersL4I1 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      1,
    );
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  const answersL4I2 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      2,
    );
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  const answersL4I3Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      3,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  const answersL4I3Q2 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningStudentAnswers,
      lessonNo,
      3,
      2,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningStudentAnswers, lessonNo]);

  return (
    <div className={styles.lesson}>
      <p className={styles.title}>
        Lesson{lessonNo < 10 ? `0${lessonNo}` : lessonNo}{" "}
        問題を取り除くために目指すことを決めよう
      </p>
      <div className={styles.inner}>
        <div className={styles.work}>
          <p className={styles.no}>WORK01</p>
          {answersL4I1.length > 0 ? (
            answersL4I1.map((answer, index) => (
              <div key={answer.id} className={styles.question}>
                {index === 0 && (
                  <p className={styles.heading}>
                    深掘りする問題を選んで、原因を考えよう。
                  </p>
                )}
                <div className={styles.tree}>
                  <p className={styles.answer}>{answer.contents.subject}</p>
                  <ul className={styles.branchList}>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>原因は？</span>
                      </div>
                      <p className={styles.answer}>{answer.contents.value}</p>
                    </li>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>調べたこと</span>
                      </div>
                      <p className={clsx(styles.answer, styles.high)}>
                        {answer.contents.research}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.question}>
              <p className={styles.heading}>
                深掘りする問題を選んで、原因を考えよう。
              </p>
              <div className={styles.tree}>
                <p className={styles.answer}>回答データがありません。</p>
                <ul className={styles.branchList}>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>原因は？</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>調べたこと</span>
                    </div>
                    <p className={clsx(styles.answer, styles.high)}>
                      回答データがありません。
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK02</p>
          {answersL4I2.length > 0 ? (
            answersL4I2.map((answer, index) => (
              <div key={answer.id} className={styles.question}>
                {index === 0 && (
                  <p className={styles.heading}>
                    原因が取り除かれた理想状況を考えよう。
                  </p>
                )}
                <div className={styles.tree}>
                  <p className={styles.answer}>{answer.contents.subject}</p>
                  <ul className={styles.branchList}>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>原因</span>
                      </div>
                      <p className={styles.answer}>{answer.contents.reason}</p>
                    </li>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>理想状況</span>
                      </div>
                      <p className={styles.answer}>{answer.contents.value}</p>
                    </li>
                  </ul>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.question}>
              <p className={styles.heading}>
                原因が取り除かれた理想状況を考えよう。
              </p>
              <div className={styles.tree}>
                <p className={styles.answer}>回答データがありません。</p>
                <ul className={styles.branchList}>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>原因</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>理想状況</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK03</p>
          <div className={styles.question}>
            <p className={styles.heading}>
              「問い」が目指す状況の実現に向けて「課題」を設定しよう。
            </p>
            <p className={styles.answer}>{answersL4I3Q1}</p>
          </div>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>
              「問い」を立て、「課題」を設定するまでに考えたことを入力しよう。
            </p>
            <p className={clsx(styles.answer, styles.high)}>{answersL4I3Q2}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Lesson4;
